import React, { useState, FC } from 'react';
import Chip from 'components/atoms/chip';
import Icon from 'components/atoms/icon';

import {
  Wrapper,
  SubHeader,
  EntityContainer,
  EntityWrapper,
  EntityHeader,
  EntityTray,
  ActionButton,
  EntityActionButton,
} from '../form.styles';

import { DescriptionList, FieldCheckbox } from 'components/atoms';
import { useTerminologyConfig } from 'context/DataContext';
import { Pagination } from 'components/molecules';
import { CarouselNavigationComponent } from 'components/molecules/carousel-navigation/CarouselNavigation.styles';
import { ReactComponent as RelatedEntityIndividual } from './../../assets/svg/Icons/RelatedEntityIndividual.svg';
import { ReactComponent as RelatedEntityCompany } from './../../assets/svg/Icons/RelatedEntityCompany.svg';
import { GatewayPermissions } from 'auth/constants/permissions';
import {
  PaginationWrapper,
  AddRelatedEntityWrapper,
  RelatedEntityOptionContainer,
  RelatedEntityInfoContainer,
  FooterContainer,
  EntityIconWrapper,
} from './LinkageThirdPartiesForm.styles';
import { useCountryHelper } from 'helpers/countriesHelper';
import { useFormContext, Controller } from 'react-hook-form';
import { useUserSettings } from 'context/DataContext';
import { LinkedThirdParty, SelectedThirdParty } from 'pages/initiate-due-diligence/types';
import {
  LinkedThirdPartyFieldsConfigs,
  ThirdPartyCreateModelResponse,
  ThirdPartyModelResponse,
} from 'types';
import { ThirdPartyBasicData } from 'pages/third-party-profile/sections/linked-thirdparties/types';
import LinkThirdpartyModal from 'pages/third-party-profile/sections/linked-thirdparties/link-thirdparty-modal';
import EditLinkageThirdpartyModal from 'pages/third-party-profile/sections/linked-thirdparties/edit-link-thirdparty-modal/EditLinkageThirdpartyModal';
import { ThirdPartyFormModal } from 'components/organisms/third-party-form-modal';
import dayjs from 'dayjs';

interface LinkageThirdPartiesFormProps {
  mainThirdPartyData: ThirdPartyModelResponse;
  individualPermitted: number;
  companyPermitted: number;
  linkedThirdParties: SelectedThirdParty[];
  onCreated: (entity: LinkedThirdParty) => void;
  onUpdated: (entity: LinkedThirdParty) => void;
  linkedThirdPartyConfigurations: LinkedThirdPartyFieldsConfigs;
}

const formatGenderOutput = {
  0: 'Male',
  1: 'Female',
  2: 'Unknown',
};

const LinkageThirdPartiesForm: FC<LinkageThirdPartiesFormProps> = ({
  mainThirdPartyData,
  individualPermitted,
  companyPermitted,
  linkedThirdParties,
  onCreated,
  onUpdated,
  linkedThirdPartyConfigurations,
}) => {
  const terminologyConfig = useTerminologyConfig();
  const totalPerPage = 10;
  const { hasPermission } = useUserSettings();
  const [indexPage, setIndexPage] = useState(1);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedLinkedThirdParty, setSelectedLinkedThirdParty] = useState(null);
  const { control, getValues, setValue } = useFormContext();
  const { getName } = useCountryHelper();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [linkModalState, setLinkModalState] = useState<{
    isOpen: boolean;
    internalId: string | null;
  }>({
    isOpen: false,
    internalId: null,
  });

  const mainThirdPartyBasicData: ThirdPartyBasicData = {
    id: mainThirdPartyData.internalId,
    name: mainThirdPartyData.name,
    thirdpartyId: mainThirdPartyData.id,
    type: mainThirdPartyData.type,
  };

  const handleClickCreateAndLinkThirdParty = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseThirdPartyCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleSuccessThirdPartyCreateModal = (thirdParty: ThirdPartyCreateModelResponse) => {
    setIsCreateModalOpen(false);
    setLinkModalState({ isOpen: true, internalId: thirdParty.internalId });
  };

  const handleCloseModal = () => {
    setLinkModalState({ isOpen: false, internalId: null });
    setOpenEditModal(false);
  };

  const generateAttributesList = (entity) => {
    const attributesList = [];

    if (entity.type === 'Individual') {
      attributesList.push(
        { key: 'Alternative name', value: entity.alternativeName },
        {
          key: 'Primary country',
          value: entity.primaryCountry ? getName(entity.primaryCountry) : null,
        },
        { key: 'Gender', value: formatGenderOutput[entity.gender] },
        {
          key: 'DOB',
          value: entity.dateOfBirth ? dayjs(entity.dateOfBirth).format('YYYY-MM-DD') : null,
        },
        { key: 'Address', value: entity.addressLineOne },
        { key: 'Post/Zip code', value: entity.zipCode },
        { key: 'City', value: entity.city },
        { key: 'State/Region/Province', value: entity.county },
        {
          key: 'Country 2',
          value: entity.countryTwo ? getName(entity.countryTwo) : null,
        },
        {
          key: 'Country 3',
          value: entity.countryThree ? getName(entity.countryThree) : null,
        },
        {
          key: 'Nationality',
          value: entity.nationality ? entity.nationality : null,
        },
        {
          key: 'Passport Number',
          value: entity.passportNumber,
        },
        { key: 'National ID number', value: entity.nationalIDNumber },
        { key: 'Other ID number', value: entity.otherIDNumber }
      );
    } else if (entity.type === 'Business') {
      attributesList.push(
        { key: 'Alternative name', value: entity.alternativeName },
        {
          key: 'Primary country',
          value: entity.primaryCountry ? getName(entity.primaryCountry) : null,
        },
        { key: 'Address', value: entity.addressLineOne },
        { key: 'Post/Zip code', value: entity.zipCode },
        { key: 'City', value: entity.city },
        {
          key: 'Country 2',
          value: entity.countryTwo ? getName(entity.countryTwo) : null,
        },
        {
          key: 'Country 3',
          value: entity.countryThree ? getName(entity.countryThree) : null,
        },
        { key: 'DUNS', value: entity.duns },
        { key: 'Registration Number', value: entity.registrationNumber },
        { key: 'SWIFT', value: entity.swift }
      );
    }

    return attributesList;
  };

  const handleOpenEditLinkageModal = (entity) => {
    const selectedThirdParty: ThirdPartyBasicData = {
      id: entity.internalId,
      name: entity.name,
      thirdpartyId: entity.id,
      type: entity.type,
    };
    setSelectedLinkedThirdParty(selectedThirdParty);
    setOpenEditModal(true);
  };

  const canAddRelatedParty = (entity) => {
    const allowedIndividualLimit = 10;
    const allowedCompanyLimit = 10;

    if (entity.type === 'Individual') {
      return (
        entity.isChecked ||
        linkedThirdParties.filter((e) => e.isChecked && e.type === 'Individual').length <
          allowedIndividualLimit
      );
    }
    if (entity.type === 'Business') {
      return (
        entity.isChecked ||
        linkedThirdParties.filter((e) => e.isChecked && e.type === 'Business').length <
          allowedCompanyLimit
      );
    }
  };

  const handleSelectedThirdParty = (id) => {
    const selectedParties = getValues('linkedThirdParties');
    const isCurrentIdSelected = selectedParties.some((en) => en === id);
    isCurrentIdSelected
      ? setValue('linkedThirdParties', [...selectedParties.filter((en) => en != id)])
      : setValue('linkedThirdParties', [...selectedParties, id]);
  };

  const allowedIndividualsMessage =
    individualPermitted > 0 && `${individualPermitted} individuals `;

  const allowedCompaniesMessage = companyPermitted > 0 && `${companyPermitted} companies `;

  const allowedAdditionalType = allowedIndividualsMessage && allowedCompaniesMessage && ' and ';

  return (
    <Wrapper>
      <SubHeader>
        Select up to <strong>{allowedIndividualsMessage}</strong>
        {allowedAdditionalType}
        <strong>{allowedCompaniesMessage}</strong>
        to be included in the report investigation.
      </SubHeader>
      <>
        <Controller
          control={control}
          name="linkedThirdParties"
          render={({ field: { value } }) => {
            return (
              <EntityWrapper>
                {linkedThirdParties.map((entity, index) => {
                  if (
                    index >= (indexPage - 1) * totalPerPage &&
                    index < (indexPage - 1) * totalPerPage + totalPerPage
                  ) {
                    return (
                      <EntityContainer key={index} selected={value}>
                        <RelatedEntityOptionContainer>
                          <FieldCheckbox
                            key={`linkedThirdParties.${index}`}
                            ariaLabel={entity.name}
                            data-testid={`re-${entity.id}`}
                            id={entity?.id?.toString()}
                            checked={value?.some((id) => id === entity.id)}
                            disabled={!canAddRelatedParty(entity)}
                            onChange={() => {
                              handleSelectedThirdParty(entity.id);
                            }}
                          />
                          <RelatedEntityInfoContainer>
                            <EntityHeader marginBottom="0px">
                              <EntityIconWrapper background={true}>
                                {entity?.type === 'Individual' ? (
                                  <RelatedEntityIndividual />
                                ) : (
                                  <RelatedEntityCompany />
                                )}
                              </EntityIconWrapper>
                              <strong>{entity?.name?.toLowerCase()}</strong>
                              {entity?.jobTitle && <Chip className="chip">{entity?.jobTitle}</Chip>}
                              <EntityTray>
                                {hasPermission(GatewayPermissions.EditThirdParty) && (
                                  <>
                                    <EntityActionButton
                                      type="button"
                                      onClick={() => handleOpenEditLinkageModal(entity)}
                                      aria-label="Edit linked thirdparty"
                                    >
                                      <Icon
                                        icon="edit-pencil"
                                        size="xxs"
                                        cursor="pointer"
                                        aria-hidden
                                      />
                                    </EntityActionButton>
                                  </>
                                )}
                              </EntityTray>
                            </EntityHeader>
                            <DescriptionList
                              items={generateAttributesList(entity)}
                              enableShowMore={true}
                              emptyValue={' '}
                              valueFontWeight={'bold'}
                              fullInformation={true}
                            />
                          </RelatedEntityInfoContainer>
                        </RelatedEntityOptionContainer>
                      </EntityContainer>
                    );
                  }
                })}
              </EntityWrapper>
            );
          }}
        />
        <FooterContainer>
          <AddRelatedEntityWrapper>
            {hasPermission(GatewayPermissions.CreateThirdParty) && (
              <ActionButton type="button" onClick={() => handleClickCreateAndLinkThirdParty()}>
                <Icon className="icon" icon="plus-circle" size="xs" cursor="pointer" />
                Create and link a {terminologyConfig?.thirdPartySingularNaming}
              </ActionButton>
            )}
            <LinkThirdpartyModal
              title={
                `Link a ` +
                terminologyConfig?.thirdPartySingularNaming +
                ` to ` +
                mainThirdPartyBasicData?.name
              }
              mainThirdparty={mainThirdPartyBasicData}
              mainThirdPartyId={mainThirdPartyData?.id}
              activeThirdPartyInternalId={linkModalState.internalId}
              isOpen={linkModalState.isOpen}
              onClose={handleCloseModal}
              onLinked={(entity) => onCreated(entity)}
              linkedThirdPartyConfigurations={linkedThirdPartyConfigurations}
            />
            {selectedLinkedThirdParty && (
              <EditLinkageThirdpartyModal
                title={`Edit ` + terminologyConfig?.thirdPartySingularNaming + ` Linkage`}
                isOpen={openEditModal}
                onClose={handleCloseModal}
                mainThirdparty={mainThirdPartyBasicData}
                linkedThirdParty={selectedLinkedThirdParty}
                onLinked={(entity) => onUpdated(entity)}
                linkedThirdPartyConfigurations={linkedThirdPartyConfigurations}
              />
            )}
            {isCreateModalOpen && (
              <ThirdPartyFormModal
                isOpen={true}
                onClose={handleCloseThirdPartyCreateModal}
                onSuccess={handleSuccessThirdPartyCreateModal}
                isChild={true}
              />
            )}
          </AddRelatedEntityWrapper>
          <PaginationWrapper>
            {linkedThirdParties?.length > 0 && (
              <CarouselNavigationComponent dark={true}>
                <Pagination
                  setCurrentPage={setIndexPage}
                  totalPages={Math.ceil(linkedThirdParties?.length / totalPerPage)}
                  currentPage={indexPage}
                  label={'Screening pagination'}
                  onPageChange={({ currentPage }) => setIndexPage(currentPage)}
                  useDots={true}
                  pagesToShow={-1}
                ></Pagination>
              </CarouselNavigationComponent>
            )}
          </PaginationWrapper>
        </FooterContainer>
      </>
    </Wrapper>
  );
};

export default LinkageThirdPartiesForm;
